import React, { useState, useRef, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Call } from '../types/Call';

interface EndCallModalProps {
  call: Call;
  duration: number;
  onEndCall: (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => void;
  onReschedule: (callId: number, newDate: string, reason: string) => void;
  onCancelCall: (callId: number, reason: string) => void;
  onClose: () => void;
}

const EndCallModal: React.FC<EndCallModalProps> = ({ 
  call, 
  duration, 
  onEndCall, 
  onReschedule, 
  onCancelCall,
  onClose 
}) => {
  const [showEndOptions, setShowEndOptions] = useState(true);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [summary, setSummary] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [callPickedUp, setCallPickedUp] = useState<boolean | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const handleCallPickedUp = () => {
    setCallPickedUp(true);
    setShowEndOptions(false);
    setShowSummaryModal(true);
  };

  const handleCallNotPickedUp = () => {
    setCallPickedUp(false);
    setShowEndOptions(false);
    setShowRescheduleModal(true);
  };

  const handleSummarySubmit = () => {
    if (!summary.trim()) {
      return;
    }
    onEndCall(call.id, summary, callPickedUp!, duration);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      return;
    }

    const newDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
    
    // If call was picked up, complete the current call first, then reschedule
    if (callPickedUp === true && summary.trim()) {
      onEndCall(call.id, summary, true, duration);
    }
    
    // For picked up calls, use summary as the reason if rescheduleReason is empty
    const reasonToUse = rescheduleReason.trim() || summary.trim() || 'Rescheduled';
    
    onReschedule(call.id, newDateTime.toISOString(), reasonToUse);
    setShowRescheduleModal(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  const handleCancelCall = () => {
    if (!rescheduleReason.trim()) {
      return;
    }
    onCancelCall(call.id, rescheduleReason);
    setShowRescheduleModal(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  return (
    <>
      {/* End Call Options Modal */}
      {showEndOptions && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">How did the call go?</h4>

              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-blue-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Call Duration</div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleCallPickedUp}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Call Picked Up
                </button>

                <button
                  onClick={handleCallNotPickedUp}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  Didn't Pick Up
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal (for picked up calls) */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Call Summary</h4>

              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-green-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Final Duration</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Call Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={4}
                  placeholder="What was discussed during the call?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSummaryModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSummaryModal(false);
                    setShowRescheduleModal(true);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Reschedule
                </button>
                <button
                  onClick={handleSummarySubmit}
                  disabled={!summary.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule/Cancel Modal (for not picked up calls) */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Call Not Picked Up</h4>

              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-red-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Call Duration</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                    <input
                      type="time"
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {callPickedUp ? 'Reason for Rescheduling' : 'Reason'}
                  </label>
                  <textarea
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    rows={3}
                    placeholder={callPickedUp ? 'Optional: Add notes about rescheduling (e.g., customer needs more time)' : "Why didn't they pick up? (e.g., 'Did not pick up - will reschedule')"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-between space-x-3 mt-6">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>

                <div className="flex space-x-2">
                  <button
                    onClick={handleCancelCall}
                    disabled={!rescheduleReason.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>

                  <button
                    onClick={handleReschedule}
                    disabled={!rescheduleDate || !rescheduleTime || (!rescheduleReason.trim() && callPickedUp !== true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Schedule Call
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EndCallModal;
